import { createEmptyLicenses } from '../models/index.js';
import { LICENSE_LIMIT } from '../config/constants.js';
import { uploadClinicLogo } from '../utils/imageUpload.js';
import { devLog, devError, devWarn } from '../utils/logger.js';
import { getFirebaseErrorMessage } from '../utils/firebaseErrors.js';
import { adminApiFetch } from '../utils/adminApi.js';
import { buildApiUrl } from '../utils/apiBase.js';
import { notifyUnauthorized } from '../utils/authController.js';
import { normalizeLicenseKey } from '../utils/licenseKeyUtils.js';

/**
 * 管理者用 Repository（API 一本化）
 */
export class AdminRepository {
  static USER_LIST_CACHE_TTL_MS = 2 * 60 * 1000;
  static CLINIC_LIST_CACHE_TTL_MS = 2 * 60 * 1000;
  static CLINICS_CACHE_TTL_MS = 5 * 60 * 1000;

  constructor() {
    this.licenseConfigCache = null;
    this.roleListCache = null;
    this.clinicsCache = null;
    this.clinicsCacheAt = 0;
    this.userListCache = new Map();
    this.clinicListCache = new Map();
  }

  static instance = null;

  static getInstance() {
    if (!AdminRepository.instance) {
      AdminRepository.instance = new AdminRepository();
    }
    return AdminRepository.instance;
  }

  buildUserListCacheKey({
    search = '',
    page = 1,
    limit = 25,
    clinicId = null,
    role = null,
    status = null,
    sortKey = 'clinic',
    sortOrder = 'asc',
  } = {}) {
    return JSON.stringify({ search, page, limit, clinicId, role, status, sortKey, sortOrder });
  }

  peekUserListCache(params) {
    const key = this.buildUserListCacheKey(params);
    const hit = this.userListCache.get(key);
    if (hit && Date.now() - hit.at < AdminRepository.USER_LIST_CACHE_TTL_MS) {
      return hit.data;
    }
    return null;
  }

  invalidateUserListCache() {
    this.userListCache.clear();
  }

  buildClinicListCacheKey({
    search = '',
    page = 1,
    limit = 25,
    filterDemo = 'all',
    filterDeleted = 'active',
    sortKey = 'registeredAt',
    sortOrder = 'desc',
  } = {}) {
    return JSON.stringify({ search, page, limit, filterDemo, filterDeleted, sortKey, sortOrder });
  }

  peekClinicListCache(params) {
    const key = this.buildClinicListCacheKey(params);
    const hit = this.clinicListCache.get(key);
    if (hit && Date.now() - hit.at < AdminRepository.CLINIC_LIST_CACHE_TTL_MS) {
      const data = hit.data;
      const hasValidTotal = Number.isFinite(Number(data?.total));
      const clinics = data?.clinics || [];
      const statsComplete = clinics.length === 0
        || clinics.every((c) => c.listStats && typeof c.listStats.userCount === 'number');
      if (hasValidTotal && statsComplete) {
        return data;
      }
    }
    return null;
  }

  invalidateClinicListCache() {
    this.clinicListCache.clear();
  }

  invalidateClinicsCache() {
    this.clinicsCache = null;
    this.clinicsCacheAt = 0;
    this.invalidateClinicListCache();
  }

  // ==================== クリニック管理 ====================

  async getAllClinics({ forceRefresh = false } = {}) {
    if (
      !forceRefresh
      && this.clinicsCache
      && Date.now() - this.clinicsCacheAt < AdminRepository.CLINICS_CACHE_TTL_MS
    ) {
      return this.clinicsCache;
    }
    try {
      const data = await adminApiFetch('/api/admin/clinics');
      const clinics = data.clinics || [];
      this.clinicsCache = clinics;
      this.clinicsCacheAt = Date.now();
      devLog('全クリニック取得完了:', clinics.length + '件');
      return clinics;
    } catch (error) {
      devError('全クリニック取得エラー:', error);
      throw error;
    }
  }

  async getClinicInvitationStats(clinicIds) {
    try {
      const data = await adminApiFetch('/api/admin/invitation-stats', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ clinicIds })
      });
      return data.stats || {};
    } catch (error) {
      devError('招待情報一括取得エラー:', error);
      return {};
    }
  }

  async getClinicDetail(clinicId) {
    try {
      return await adminApiFetch(`/api/admin/clinics/${encodeURIComponent(clinicId)}`);
    } catch (error) {
      devError('クリニック詳細取得エラー:', error);
      throw error;
    }
  }

  async getClinicPaymentHistory(clinicId, { limit = 20 } = {}) {
    try {
      const params = new URLSearchParams();
      if (limit) params.set('limit', String(limit));
      const qs = params.toString();
      return await adminApiFetch(
        `/api/admin/clinics/${encodeURIComponent(clinicId)}/payment-history${qs ? `?${qs}` : ''}`,
      );
    } catch (error) {
      devError('クリニック決済履歴取得エラー:', error);
      throw error;
    }
  }

  async createClinic(clinicData) {
    try {
      const { logoFile, ...rest } = clinicData;
      const payload = { ...rest };
      delete payload.licenseLimit;

      const data = await adminApiFetch('/api/admin/clinics', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      const clinicId = data.clinicId;
      const code = data.code || null;
      if (logoFile && clinicId) {
        await uploadClinicLogo(clinicId, logoFile);
      }

      devLog('クリニック作成完了:', clinicId);
      this.invalidateClinicsCache();
      return { clinicId, code };
    } catch (error) {
      devError('クリニック作成エラー:', error);
      throw new Error(getFirebaseErrorMessage(error));
    }
  }

  async updateClinic(clinicId, updateData) {
    try {
      const { logoFile, ...rest } = { ...updateData };
      const payload = { ...rest };
      delete payload.logoFile;

      if (logoFile) {
        const { logoUrl } = await uploadClinicLogo(clinicId, logoFile);
        payload.logoUrl = logoUrl;
      }

      await adminApiFetch(`/api/admin/clinics/${clinicId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      devLog('クリニック更新完了:', clinicId);
      this.invalidateClinicsCache();
    } catch (error) {
      devError('クリニック更新エラー:', error);
      throw error;
    }
  }

  async deleteClinic(clinicId, reason) {
    try {
      const result = await adminApiFetch(`/api/admin/clinics/${clinicId}`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ reason }),
      });
      this.invalidateClinicsCache();
      return result;
    } catch (error) {
      devError('クリニック削除エラー:', error);
      throw error;
    }
  }

  async restoreClinic(clinicId, reason) {
    try {
      const result = await adminApiFetch(`/api/admin/clinics/${clinicId}/restore`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ reason }),
      });
      this.invalidateClinicsCache();
      return result;
    } catch (error) {
      devError('クリニック復元エラー:', error);
      throw error;
    }
  }

  async createDemoClinicAccount(count = 1) {
    try {
      const data = await adminApiFetch('/api/admin/create-demo-clinic', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ count })
      });
      this.invalidateClinicsCache();
      return data;
    } catch (error) {
      devError('デモクリニック作成エラー:', error);
      throw error;
    }
  }

  async getDemoClinics() {
    try {
      const data = await adminApiFetch('/api/admin/demo-clinics');
      return data.demos || [];
    } catch (error) {
      devError('デモ一覧取得エラー:', error);
      throw error;
    }
  }

  async deleteDemoClinic(clinicId, reason) {
    try {
      await adminApiFetch(`/api/admin/demo-clinics/${clinicId}`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ reason }),
      });
      this.invalidateClinicsCache();
    } catch (error) {
      devError('デモ削除エラー:', error);
      throw error;
    }
  }

  async getAvailableLicenses() {
    try {
      const config = await this.getLicenseConfig();
      const seen = new Set();
      const names = [];
      for (const item of config.names || []) {
        const rawName = typeof item === 'string' ? item : item?.name;
        const name = typeof rawName === 'string' ? rawName.trim() : '';
        if (!name) continue;
        // 未登録スロットは選択肢に出さない（isRegistered 未定義時は後方互換で含める）
        // 予約キー all/demo は isRegistered=false だが、クリニックへの付与対象として含める
        if (
          item && typeof item === 'object'
          && item.isRegistered === false
          && item.isReserved !== true
        ) continue;
        if (seen.has(name)) continue;
        seen.add(name);
        names.push(name);
      }
      return names;
    } catch (error) {
      devError('ライセンスリスト取得エラー:', error);
      throw error;
    }
  }

  async getRegisteredLicenseKeys() {
    const config = await this.getLicenseConfig();
    return (config.names || [])
      .filter((item) => item.isRegistered)
      .map((item) => normalizeLicenseKey(item.name))
      .filter(Boolean);
  }

  async getLicenseConfig() {
    if (this.licenseConfigCache) {
      return this.licenseConfigCache;
    }

    try {
      const data = await adminApiFetch('/api/admin/license-config');
      const result = {
        limit: data.limit || LICENSE_LIMIT,
        names: data.names || []
      };
      this.licenseConfigCache = result;
      return result;
    } catch (error) {
      devWarn('ライセンス設定取得エラー（デフォルト使用）:', error);
      const names = [];
      for (let i = 1; i <= LICENSE_LIMIT; i++) {
        names.push({ key: `license${String(i).padStart(2, '0')}`, name: '' });
      }
      return { limit: LICENSE_LIMIT, names };
    }
  }

  async updateLicenseConfig(payload) {
    try {
      await adminApiFetch('/api/admin/license-config', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      this.licenseConfigCache = null;
    } catch (error) {
      devError('ライセンス設定更新エラー:', error);
      throw error;
    }
  }

  async renameLicenseRegistryKey(oldKey, newKey) {
    const data = await adminApiFetch('/api/admin/license-registry/rename', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ oldKey, newKey })
    });
    this.licenseConfigCache = null;
    return data;
  }

  async addLicenseRegistryKey(key) {
    const data = await adminApiFetch('/api/admin/license-registry/add', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ key })
    });
    this.licenseConfigCache = null;
    return data;
  }

  async removeLicenseRegistryKey(key, purgeAccounts = false) {
    const data = await adminApiFetch('/api/admin/license-registry/remove', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ key, purgeAccounts })
    });
    this.licenseConfigCache = null;
    return data;
  }

  async reorderLicenseRegistryKeys(keys) {
    const data = await adminApiFetch('/api/admin/license-registry/reorder', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ keys })
    });
    this.licenseConfigCache = null;
    return data;
  }

  async getRoleList({ forceRefresh = false } = {}) {
    if (!forceRefresh && this.roleListCache) {
      return this.roleListCache;
    }
    try {
      const data = await adminApiFetch('/api/admin/roles');
      const roles = Array.isArray(data.roles) ? data.roles : [];
      this.roleListCache = roles;
      return roles;
    } catch (error) {
      devError('ロール一覧取得エラー:', error);
      throw error;
    }
  }

  async updateRoleList(roles) {
    try {
      const data = await adminApiFetch('/api/admin/roles', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ roles })
      });
      this.roleListCache = Array.isArray(data.roles) ? data.roles : [];
      return data.roles || [];
    } catch (error) {
      devError('ロール更新エラー:', error);
      throw error;
    }
  }

  async getHubSettings() {
    try {
      const data = await adminApiFetch('/api/admin/hub-settings');
      return {
        isBetaPeriod: data.isBetaPeriod === true,
        trialPeriodDays: Number(data.trialPeriodDays) || 14,
        betaTrialPeriodDays: Number(data.betaTrialPeriodDays) || 30,
        pastDueGraceDays: Number(data.pastDueGraceDays) || 7,
        emailTestOverrideTo: data.emailTestOverrideTo || '',
        emailOverrideEnabledInProd: data.emailOverrideEnabledInProd === true,
        emailOverrideStatus: data.emailOverrideStatus || null,
        userHubNavVisibility: data.userHubNavVisibility || null,
      };
    } catch (error) {
      devError('ハブ設定取得エラー:', error);
      throw error;
    }
  }

  async updateHubSettings(settings) {
    try {
      const data = await adminApiFetch('/api/admin/hub-settings', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(settings)
      });
      return {
        isBetaPeriod: data.isBetaPeriod === true,
        trialPeriodDays: Number(data.trialPeriodDays) || 14,
        betaTrialPeriodDays: Number(data.betaTrialPeriodDays) || 30,
        pastDueGraceDays: Number(data.pastDueGraceDays) || 7,
        emailTestOverrideTo: data.emailTestOverrideTo || '',
        emailOverrideEnabledInProd: data.emailOverrideEnabledInProd === true,
        emailOverrideStatus: data.emailOverrideStatus || null,
        userHubNavVisibility: data.userHubNavVisibility || null,
        message: data.message || '',
        betaExitAlerts: Array.isArray(data.betaExitAlerts) ? data.betaExitAlerts : []
      };
    } catch (error) {
      devError('ハブ設定更新エラー:', error);
      throw error;
    }
  }

  async addClinicLicense(clinicId, licenseName, options = {}) {
    return this.setClinicLicense(clinicId, licenseName, options);
  }

  async detectLicenseDriftForClinic(clinicId) {
    try {
      const data = await adminApiFetch(`/api/admin/detect-license-drift/${clinicId}`);
      return {
        checked: data.checked || 0,
        repaired: data.repaired || 0,
        issues: data.issues || []
      };
    } catch (error) {
      devError('ドリフト検出エラー:', error);
      return { checked: 0, repaired: 0, issues: [] };
    }
  }

  async removeClinicLicense(clinicId, licenseName) {
    try {
      const data = await adminApiFetch('/api/admin/set-clinic-license', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ clinicId, licenseName, remove: true })
      });
      devLog('ライセンス削除完了:', { clinicId, licenseName });
      this.invalidateClinicListCache();
      return data;
    } catch (error) {
      devError('ライセンス削除エラー:', error);
      throw error;
    }
  }

  async setClinicLicense(clinicId, licenseName, options = {}) {
    try {
      const {
        status = 'active',
        trialEndAt = null,
        billingDeferralUntil,
        billingDeferralReason,
        clearStripeSubscriptionId,
        repairStripeSubscription,
      } = options;

      const body = { clinicId, licenseName, status, trialEndAt };
      if (billingDeferralUntil !== undefined) body.billingDeferralUntil = billingDeferralUntil;
      if (billingDeferralReason !== undefined) body.billingDeferralReason = billingDeferralReason;
      if (clearStripeSubscriptionId === true) body.clearStripeSubscriptionId = true;
      if (repairStripeSubscription === true) body.repairStripeSubscription = true;

      const data = await adminApiFetch('/api/admin/set-clinic-license', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });

      devLog('ライセンス設定完了:', { clinicId, licenseName, status });
      this.invalidateClinicListCache();
      return data;
    } catch (error) {
      devError('ライセンス設定エラー:', error);
      throw error;
    }
  }

  async grantLicenseFreeDays(clinicId, licenseName, days) {
    try {
      const data = await adminApiFetch('/api/admin/grant-license-free-days', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ clinicId, licenseName, days }),
      });
      devLog('無料日数付与完了:', { clinicId, licenseName, days });
      this.invalidateClinicListCache();
      return data;
    } catch (error) {
      devError('無料日数付与エラー:', error);
      throw error;
    }
  }

  async clearClinicLicenseStripeLink(clinicId, licenseName, options = {}) {
    const { status = 'active' } = options;
    return this.setClinicLicense(clinicId, licenseName, {
      status,
      clearStripeSubscriptionId: true,
    });
  }

  async repairClinicLicenseStripeLink(clinicId, licenseName, options = {}) {
    const { status = 'active' } = options;
    return this.setClinicLicense(clinicId, licenseName, {
      status,
      repairStripeSubscription: true,
    });
  }

  async getClinicLicenseContracts(clinicId, options = {}) {
    const { throwOnError = false } = options;
    try {
      const data = await adminApiFetch(`/api/admin/clinic-license-contracts/${clinicId}`);
      return data.contracts || [];
    } catch (error) {
      devError('ライセンス契約取得エラー:', error);
      if (throwOnError) throw error;
      return [];
    }
  }

  async getClinic(clinicId) {
    try {
      const data = await adminApiFetch(`/api/admin/clinics/${clinicId}`);
      return data.clinic || null;
    } catch (error) {
      devError('クリニック取得エラー:', error);
      throw error;
    }
  }

  // ==================== ユーザー管理 ====================

  async getAllUsers() {
    try {
      const data = await adminApiFetch('/api/admin/users');
      const users = (data.users || []).map((u) => ({
        uid: u.uid || u.id,
        ...u
      }));

      users.sort((a, b) => {
        const dateA = a.createdAt?.toDate?.() || new Date(a.createdAt || 0);
        const dateB = b.createdAt?.toDate?.() || new Date(b.createdAt || 0);
        return dateB - dateA;
      });

      devLog('全ユーザー取得完了:', users.length + '件');
      return users;
    } catch (error) {
      devError('全ユーザー取得エラー:', error);
      throw error;
    }
  }

  async createUser(userData) {
    try {
      const { password, userId, displayName, clinicId, role, isActive } = userData;

      if (!userId || !clinicId) {
        throw new Error('ユーザーIDとクリニックIDは必須です');
      }
      if (!password) {
        throw new Error('パスワードは必須です');
      }

      const data = await adminApiFetch('/api/admin/create-user', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: '',
          password,
          userId,
          displayName: displayName || '',
          clinicId,
          role: role || 'staff',
          isActive: isActive !== undefined ? isActive : true
        })
      });

      devLog('ユーザー作成完了:', data.uid);
      this.invalidateUserListCache();
      this.invalidateClinicListCache();
      return data;
    } catch (error) {
      devError('ユーザー作成エラー:', error);
      throw new Error(getFirebaseErrorMessage(error));
    }
  }

  async updateUser(uid, updateData) {
    return this.putUser(uid, updateData);
  }

  async updateUserRole(uid, role) {
    if (!role || typeof role !== 'string' || !role.trim()) {
      throw new Error('ロールを指定してください');
    }
    return this.putUser(uid, { role: role.trim() });
  }

  async setUserActive(uid, isActive) {
    return this.putUser(uid, { isActive });
  }

  async changeUserClinic(uid, clinicId) {
    return this.putUser(uid, { clinicId });
  }

  async putUser(uid, payload) {
    try {
      await adminApiFetch(`/api/admin/users/${uid}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      devLog('ユーザー更新完了:', uid);
      this.invalidateUserListCache();
      this.invalidateClinicListCache();
    } catch (error) {
      devError('ユーザー更新エラー:', error);
      throw error;
    }
  }

  computeClinicUserStatsFromUsers(allUsers, clinicId) {
    const clinicUsers = allUsers.filter((u) => u.clinicId === clinicId);
    const byRole = {};
    for (const user of clinicUsers) {
      const role = (user.role && String(user.role).trim()) || 'unknown';
      byRole[role] = (byRole[role] || 0) + 1;
    }
    return {
      total: clinicUsers.length,
      byRole
    };
  }

  async getClinicUserStats(clinicId) {
    try {
      const data = await adminApiFetch(`/api/admin/clinics/${clinicId}/user-stats`);
      return {
        total: data.total || 0,
        byRole: data.byRole || {}
      };
    } catch (error) {
      devError('クリニックユーザー統計取得エラー:', error);
      return {
        total: 0,
        byRole: {}
      };
    }
  }

  async getDashboardStats() {
    try {
      return await adminApiFetch('/api/admin/dashboard-stats');
    } catch (error) {
      devError('統計情報取得エラー:', error);
      throw error;
    }
  }

  async getStatisticsSummary(targetDateKey = '') {
    try {
      const qs = targetDateKey ? `?targetDateKey=${encodeURIComponent(targetDateKey)}` : '';
      return await adminApiFetch(`/api/admin/statistics/summary${qs}`);
    } catch (error) {
      devError('統計ログ取得エラー:', error);
      throw error;
    }
  }

  async getAdminHubStatisticsDetails(params = {}) {
    try {
      const qs = new URLSearchParams();
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          qs.set(key, String(value));
        }
      });
      const query = qs.toString();
      return await adminApiFetch(`/api/admin/statistics/admin-hub/details${query ? `?${query}` : ''}`);
    } catch (error) {
      devError('AdminHub統計詳細取得エラー:', error);
      throw error;
    }
  }

  async getStatisticsProducts(params = {}) {
    try {
      const qs = new URLSearchParams();
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          qs.set(key, String(value));
        }
      });
      const query = qs.toString();
      return await adminApiFetch(`/api/admin/statistics/products${query ? `?${query}` : ''}`);
    } catch (error) {
      devError('製品別統計ログ取得エラー:', error);
      throw error;
    }
  }

  async getStatisticsProductDetail(productKey, params = {}) {
    try {
      const qs = new URLSearchParams();
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          qs.set(key, String(value));
        }
      });
      const query = qs.toString();
      const encodedKey = encodeURIComponent(productKey);
      return await adminApiFetch(`/api/admin/statistics/products/${encodedKey}${query ? `?${query}` : ''}`);
    } catch (error) {
      devError('製品詳細統計ログ取得エラー:', error);
      throw error;
    }
  }

  async getStatisticsProductTimeseries(productKey, params = {}) {
    try {
      const qs = new URLSearchParams();
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          qs.set(key, String(value));
        }
      });
      const query = qs.toString();
      const encodedKey = encodeURIComponent(productKey);
      return await adminApiFetch(`/api/admin/statistics/products/${encodedKey}/timeseries${query ? `?${query}` : ''}`);
    } catch (error) {
      devError('製品時系列統計取得エラー:', error);
      throw error;
    }
  }

  async getClinicStatisticsLogs(clinicId, params = {}) {
    try {
      const qs = new URLSearchParams();
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          qs.set(key, String(value));
        }
      });
      const query = qs.toString();
      const encodedId = encodeURIComponent(clinicId);
      return await adminApiFetch(`/api/admin/statistics/clinics/${encodedId}${query ? `?${query}` : ''}`);
    } catch (error) {
      devError('医院別統計ログ取得エラー:', error);
      throw error;
    }
  }

  async getStatisticsClinicAppTop(params = {}) {
    try {
      const qs = new URLSearchParams();
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          qs.set(key, String(value));
        }
      });
      const query = qs.toString();
      return await adminApiFetch(`/api/admin/statistics/clinic-app-top${query ? `?${query}` : ''}`);
    } catch (error) {
      devError('医院×アプリ上位統計取得エラー:', error);
      throw error;
    }
  }

  async searchClinics(query, limit = 30) {
    try {
      const qs = new URLSearchParams();
      if (query) qs.set('q', query);
      if (limit) qs.set('limit', String(limit));
      return await adminApiFetch(`/api/admin/clinics/search?${qs.toString()}`);
    } catch (error) {
      devError('クリニック検索エラー:', error);
      throw error;
    }
  }

  async getClinicUsers(clinicId) {
    try {
      const encodedId = encodeURIComponent(clinicId);
      return await adminApiFetch(`/api/admin/clinics/${encodedId}/users`);
    } catch (error) {
      devError('クリニックユーザー取得エラー:', error);
      throw error;
    }
  }

  async runDailyStatisticsLog(targetDate = '') {
    try {
      return await adminApiFetch('/api/admin/statistics/run-daily', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(targetDate ? { targetDate } : {})
      });
    } catch (error) {
      devError('統計ログ手動実行エラー:', error);
      throw error;
    }
  }

  /**
   * ダッシュボードサマリー（軽量初回ロード）
   */
  async getDashboardSummary() {
    try {
      const data = await adminApiFetch('/api/admin/dashboard-summary');
      if (data.licenseConfig) {
        this.licenseConfigCache = data.licenseConfig;
      }
      return data;
    } catch (error) {
      devError('ダッシュボードサマリー取得エラー:', error);
      throw error;
    }
  }

  /**
   * ダッシュボードサマリー詳細（ロール別・お問い合わせステータス別）
   */
  async getDashboardSummaryDetails() {
    try {
      return await adminApiFetch('/api/admin/dashboard-summary-details');
    } catch (error) {
      devError('ダッシュボードサマリー詳細取得エラー:', error);
      throw error;
    }
  }

  async listClinicsPaginated(
    {
      search = '',
      page = 1,
      limit = 25,
      filterDemo = 'all',
      filterDeleted = 'active',
      sortKey = 'registeredAt',
      sortOrder = 'desc',
    } = {},
    { forceRefresh = false } = {}
  ) {
    const params = { search, page, limit, filterDemo, filterDeleted, sortKey, sortOrder };
    const cacheKey = this.buildClinicListCacheKey(params);

    if (!forceRefresh) {
      const cached = this.peekClinicListCache(params);
      if (cached) return cached;
    }

    const qsParams = new URLSearchParams();
    if (search) qsParams.set('search', search);
    if (page) qsParams.set('page', String(page));
    if (limit) qsParams.set('limit', String(limit));
    if (filterDemo && filterDemo !== 'all') qsParams.set('filterDemo', filterDemo);
    if (filterDeleted && filterDeleted !== 'active') qsParams.set('filterDeleted', filterDeleted);
    if (sortKey) qsParams.set('sortKey', sortKey);
    if (sortOrder) qsParams.set('sortOrder', sortOrder);
    const qs = qsParams.toString();
    const data = await adminApiFetch(`/api/admin/clinics/list${qs ? `?${qs}` : ''}`);
    this.clinicListCache.set(cacheKey, { data, at: Date.now() });
    return data;
  }

  async exportClinicList({
    search = '',
    filterDemo = 'all',
    filterDeleted = 'active',
    sortKey = 'registeredAt',
    sortOrder = 'desc',
  } = {}) {
    const qsParams = new URLSearchParams();
    if (search) qsParams.set('search', search);
    if (filterDemo && filterDemo !== 'all') qsParams.set('filterDemo', filterDemo);
    if (filterDeleted && filterDeleted !== 'active') qsParams.set('filterDeleted', filterDeleted);
    if (sortKey) qsParams.set('sortKey', sortKey);
    if (sortOrder) qsParams.set('sortOrder', sortOrder);
    const qs = qsParams.toString();
    return adminApiFetch(`/api/admin/clinics/list/export${qs ? `?${qs}` : ''}`);
  }

  async listUsersPaginated(
    {
      search = '',
      page = 1,
      limit = 25,
      startAfter = null,
      clinicId = null,
      role = null,
      status = null,
      sortKey = 'clinic',
      sortOrder = 'asc',
    } = {},
    { forceRefresh = false } = {}
  ) {
    const params = { search, page, limit, clinicId, role, status, sortKey, sortOrder };
    const cacheKey = this.buildUserListCacheKey(params);

    if (!forceRefresh) {
      const cached = this.peekUserListCache(params);
      if (cached) return cached;
    }

    const qsParams = new URLSearchParams();
    if (search) qsParams.set('search', search);
    if (page) qsParams.set('page', String(page));
    if (limit) qsParams.set('limit', String(limit));
    if (startAfter) qsParams.set('startAfter', startAfter);
    if (clinicId) qsParams.set('clinicId', clinicId);
    if (role) qsParams.set('role', role);
    if (status) qsParams.set('status', status);
    if (sortKey) qsParams.set('sortKey', sortKey);
    if (sortOrder) qsParams.set('sortOrder', sortOrder);
    const qs = qsParams.toString();
    const data = await adminApiFetch(`/api/admin/users/list${qs ? `?${qs}` : ''}`);
    this.userListCache.set(cacheKey, { data, at: Date.now() });
    return data;
  }

  async listInquiriesPaginated({
    search = '',
    limit = 50,
    startAfter = null,
    status = null,
    statuses = null,
    sources = null,
    statusSortOrder = '',
    view = 'threads',
  } = {}) {
    const params = new URLSearchParams();
    if (search) params.set('search', search);
    if (limit) params.set('limit', String(limit));
    if (startAfter) params.set('startAfter', startAfter);
    if (statuses?.length) params.set('statuses', statuses.join(','));
    else if (statuses !== null && Array.isArray(statuses) && statuses.length === 0) params.set('statuses', '');
    else if (status) params.set('statuses', status);
    if (sources?.length) params.set('sources', sources.join(','));
    else if (sources !== null && Array.isArray(sources) && sources.length === 0) params.set('sources', '');
    if (statusSortOrder) params.set('statusSortOrder', statusSortOrder);
    if (view) params.set('view', view);
    const qs = params.toString();
    return adminApiFetch(`/api/admin/contact/inquiries/list${qs ? `?${qs}` : ''}`);
  }

  async fetchInquiryStats() {
    return adminApiFetch('/api/admin/contact/inquiries/stats');
  }

  async fetchInquiryThread(threadId, { inquiryId = null } = {}) {
    const tryThread = async (id) => {
      const data = await adminApiFetch(`/api/admin/contact/threads/${encodeURIComponent(id)}`);
      if (data?.thread) return data;
      const err = new Error('スレッド詳細の形式が不正です。');
      err.status = 502;
      throw err;
    };

    try {
      return await tryThread(threadId);
    } catch (err) {
      const fallbackId = inquiryId || threadId;
      if (fallbackId && (err.status === 404 || err.status === 502 || String(err.message).includes('不正な応答'))) {
        const data = await adminApiFetch(`/api/admin/contact/inquiries/${encodeURIComponent(fallbackId)}/thread`);
        if (data?.thread) return data;
      }
      throw err;
    }
  }

  async fetchInquiryThreadByInquiryId(inquiryId) {
    return adminApiFetch(`/api/admin/contact/inquiries/${encodeURIComponent(inquiryId)}/thread`);
  }

  async fetchActiveSupportNumbers() {
    return adminApiFetch('/api/admin/contact/support-numbers/active');
  }

  async issueSupportNumber(threadId, { regenerate = false } = {}) {
    return adminApiFetch(`/api/admin/contact/threads/${encodeURIComponent(threadId)}/support-number`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ regenerate }),
    });
  }

  async assignInquiryToThread(inquiryId, { threadId, supportNumber }) {
    return adminApiFetch(`/api/admin/contact/inquiries/${encodeURIComponent(inquiryId)}/assign-thread`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ threadId, supportNumber }),
    });
  }

  async sendInquiryReply(threadId, { body, subject }) {
    return adminApiFetch(`/api/admin/contact/threads/${encodeURIComponent(threadId)}/reply`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ body, subject }),
    });
  }

  async generateInquiryReplyDraft(threadId, { toneHint = '', extraInstruction = '' } = {}) {
    return adminApiFetch(`/api/admin/contact/threads/${encodeURIComponent(threadId)}/generate-reply`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ toneHint, extraInstruction }),
    });
  }

  async syncInboundInquiryEmails({ maxMessages = 50, onProgress, stream = Boolean(onProgress) } = {}) {
    const useStream = stream === true;
    const response = await fetch(buildApiUrl('/api/admin/contact/inbound/sync'), {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        ...(useStream ? { Accept: 'application/x-ndjson' } : { Accept: 'application/json' }),
      },
      body: JSON.stringify({ maxMessages, stream: useStream }),
    });

    if (!response.ok) {
      if (response.status === 401) notifyUnauthorized();
      const contentType = response.headers.get('content-type') || '';
      const data = contentType.includes('application/json')
        ? await response.json().catch(() => ({}))
        : {};
      const err = new Error(data?.message || `API エラー (${response.status})`);
      err.status = response.status;
      err.code = data?.error || null;
      throw err;
    }

    if (!useStream) {
      const contentType = response.headers.get('content-type') || '';
      if (!contentType.includes('application/json')) {
        const err = new Error(`API が不正な応答を返しました (${response.status})。バックエンドを再起動してください。`);
        err.status = response.status;
        throw err;
      }
      return response.json();
    }

    if (!response.body) {
      const err = new Error('受信メール取込の進捗ストリームを取得できませんでした');
      err.status = 502;
      throw err;
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    let finalResult = null;

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split('\n');
      buffer = lines.pop() || '';
      for (const line of lines) {
        const trimmed = line.trim();
        if (!trimmed) continue;
        let evt;
        try {
          evt = JSON.parse(trimmed);
        } catch {
          continue;
        }
        onProgress?.(evt);
        if (evt.phase === 'done') {
          finalResult = evt;
        }
      }
    }

    const trailing = buffer.trim();
    if (trailing) {
      try {
        const evt = JSON.parse(trailing);
        onProgress?.(evt);
        if (evt.phase === 'done') finalResult = evt;
      } catch {
        // ignore
      }
    }

    if (!finalResult) {
      const err = new Error('受信メール取込が完了結果を返しませんでした');
      err.status = 502;
      throw err;
    }

    if (finalResult.success === false) {
      const err = new Error(finalResult.errors?.[0] || '受信メールの取込に失敗しました');
      err.status = 500;
      throw err;
    }

    return finalResult;
  }

  async fetchClinicStatsBatch(clinicIds) {
    const data = await adminApiFetch('/api/admin/clinic-stats-batch', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ clinicIds }),
    });
    return data.stats || {};
  }

  /**
   * ダッシュボード一括データ（clinics / users / stats / clinicStats / licenseConfig）
   */
  async getDashboardData() {
    try {
      const data = await adminApiFetch('/api/admin/dashboard-data');
      if (data.licenseConfig) {
        this.licenseConfigCache = data.licenseConfig;
      }
      devLog('ダッシュボード一括データ取得完了');
      return data;
    } catch (error) {
      devError('ダッシュボード一括データ取得エラー:', error);
      throw error;
    }
  }

  async getClinicLicenseStatsForClinic(clinic, licenseConfig) {
    try {
      const limit = licenseConfig?.limit || LICENSE_LIMIT;
      if (!clinic) {
        throw new Error('クリニックが見つかりません');
      }

      const licenses = clinic.licenses || createEmptyLicenses(limit);
      const activeLicenses = [];
      let used = 0;

      for (let i = 1; i <= limit; i++) {
        const licenseKey = `license${String(i).padStart(2, '0')}`;
        if (licenses[licenseKey] && licenses[licenseKey] !== '') {
          activeLicenses.push({ key: licenseKey, name: licenses[licenseKey] });
          used++;
        }
      }

      const contracts = await this.getClinicLicenseContracts(clinic.id);
      return this.buildLicenseStatsResult(limit, activeLicenses, used, contracts);
    } catch (error) {
      devError('ライセンス統計取得エラー:', error);
      throw error;
    }
  }

  buildLicenseStatsResult(limit, activeLicenses, used, contracts) {
    const contractByName = new Map(
      contracts
        .filter((contract) => contract?.licenseName)
        .map((contract) => [String(contract.licenseName), contract])
    );

    const now = Date.now();
    const toMillis = (value) => {
      if (!value) return null;
      if (typeof value === 'number') return value;
      if (typeof value?.toMillis === 'function') return value.toMillis();
      if (typeof value?.toDate === 'function') return value.toDate().getTime();
      if (typeof value?.seconds === 'number') return value.seconds * 1000;
      if (typeof value?._seconds === 'number') return value._seconds * 1000;
      return null;
    };

    const isExpiredContract = (contract) => {
      if (!contract) return false;
      const status = String(contract.status || '').toLowerCase();
      if (status === 'past_due' || status === 'canceled') return true;
      if (status === 'trial') {
        const trialEndAtMs = toMillis(contract.trialEndAt);
        return typeof trialEndAtMs === 'number' && trialEndAtMs < now;
      }
      return false;
    };

    const expiredLicenses = activeLicenses.filter((license) =>
      isExpiredContract(contractByName.get(license.name))
    );

    return {
      used,
      available: limit - used,
      licenses: activeLicenses,
      expiredCount: expiredLicenses.length,
      hasExpiredLicense: expiredLicenses.length > 0
    };
  }

  async getClinicLicenseStats(clinicId) {
    try {
      const licenseConfig = await this.getLicenseConfig();
      const clinic = await this.getClinic(clinicId);
      return await this.getClinicLicenseStatsForClinic(clinic, licenseConfig);
    } catch (error) {
      devError('ライセンス統計取得エラー:', error);
      throw error;
    }
  }

  async deleteUser(uid, reason) {
    try {
      const data = await adminApiFetch('/api/admin/delete-user', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ uid, reason })
      });
      devLog('ユーザー削除完了:', uid);
      this.invalidateUserListCache();
      this.invalidateClinicListCache();
      return data;
    } catch (error) {
      devError('ユーザー削除エラー:', error);
      throw error;
    }
  }
}

export default AdminRepository;
